import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Calculator, TrendingUp } from "lucide-react";

interface ROICalculatorProps {
  children: React.ReactNode;
}

export const ROICalculator = ({ children }: ROICalculatorProps) => {
  const [plotSize, setPlotSize] = useState("242");
  const [pricePerSqYd, setPricePerSqYd] = useState("5800");
  const [rentalIncome, setRentalIncome] = useState("10000");
  const [appreciationRate, setAppreciationRate] = useState("9");
  const [holdingPeriod, setHoldingPeriod] = useState("15");
  const [showResults, setShowResults] = useState(false);

  // Calculate teak return based on plot size
  const getTeakReturn = (plotSizeSqYd: number): number => {
    if (plotSizeSqYd >= 200) return 2000000; // ₹20 Lakhs for 200+ sq yd
    if (plotSizeSqYd >= 121) return 1000000; // ₹10 Lakhs for 121-199 sq yd
    return 0; // No teak return for plots below 121 sq yd
  };

  const calculateROI = () => {
    const P = parseFloat(plotSize);
    const R = parseFloat(pricePerSqYd);
    const RI = parseFloat(rentalIncome);
    const A = parseFloat(appreciationRate);
    const Y = parseFloat(holdingPeriod);

    // Total Investment
    const investment = P * R;

    // Total Rental Income (for 5 years only)
    const rentalYears = Math.min(Y, 5);
    const totalRental = RI * rentalYears;

    // Teak Return (only if holding period >= 12 years)
    const teakValue = Y >= 12 ? getTeakReturn(P) : 0;

    // Appreciated Plot Value
    const appreciatedValue = investment * Math.pow(1 + A / 100, Y);

    // Total Return
    const totalReturn = totalRental + teakValue + appreciatedValue;

    // ROI Percentage
    const roiPercentage = ((totalReturn - investment) / investment) * 100;

    return {
      investment,
      totalRental,
      rentalYears,
      teakValue,
      appreciatedValue,
      totalReturn,
      roiPercentage,
      netProfit: totalReturn - investment,
    };
  };

  const handleCalculate = () => {
    setShowResults(true);
  };

  const results = showResults ? calculateROI() : null;

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <Dialog>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="text-2xl flex items-center gap-2">
            <Calculator className="h-6 w-6 text-primary" />
            ROI Calculator
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          <div className="space-y-4">
            <h3 className="font-semibold text-lg">Investment Details</h3>

            <div className="space-y-2">
              <Label htmlFor="plotSize">Plot Size (sq.yd)</Label>
              <Input
                id="plotSize"
                type="number"
                value={plotSize}
                onChange={(e) => setPlotSize(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="pricePerSqYd">Price per sq.yd (₹)</Label>
              <Input
                id="pricePerSqYd"
                type="number"
                value={pricePerSqYd}
                onChange={(e) => setPricePerSqYd(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="rentalIncome">Annual Rental Income (₹) <span className="text-xs text-muted-foreground">(for 5 years)</span></Label>
              <Input
                id="rentalIncome"
                type="number"
                value={rentalIncome}
                onChange={(e) => setRentalIncome(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="teakReturn">Teak Return after 12 years</Label>
              <div className="p-3 bg-muted/50 rounded-md text-sm">
                {parseFloat(plotSize) >= 200 ? (
                  <span className="text-primary font-semibold">₹20,00,000 (200+ sq yd)</span>
                ) : parseFloat(plotSize) >= 121 ? (
                  <span className="text-primary font-semibold">₹10,00,000 (121-199 sq yd)</span>
                ) : (
                  <span className="text-muted-foreground">Not applicable (below 121 sq yd)</span>
                )}
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="appreciationRate">Annual Appreciation Rate (%)</Label>
              <Input
                id="appreciationRate"
                type="number"
                value={appreciationRate}
                onChange={(e) => setAppreciationRate(e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="holdingPeriod">Holding Period (years)</Label>
              <Input
                id="holdingPeriod"
                type="number"
                value={holdingPeriod}
                onChange={(e) => setHoldingPeriod(e.target.value)}
              />
            </div>

            <Button onClick={handleCalculate} className="w-full gap-2">
              <Calculator className="h-4 w-4" />
              Calculate Returns
            </Button>
          </div>

          <div className="space-y-4">
            <h3 className="font-semibold text-lg flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-primary" />
              Results
            </h3>

            {!showResults ? (
              <Card className="border-dashed">
                <CardContent className="p-8 text-center text-muted-foreground">
                  Enter your details and click Calculate to see your potential returns
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                <Card className="border-primary/20 bg-primary/5">
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground mb-1">Total Investment</div>
                    <div className="text-2xl font-bold text-foreground">
                      {formatCurrency(results!.investment)}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardContent className="p-4 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Rental Income <span className="text-xs">({results!.rentalYears} years)</span>
                      </span>
                      <span className="font-semibold">{formatCurrency(results!.totalRental)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">
                        Teak Return {parseFloat(holdingPeriod) < 12 && "(N/A < 12 years)"}
                      </span>
                      <span className="font-semibold">{formatCurrency(results!.teakValue)}</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Appreciated Value</span>
                      <span className="font-semibold">{formatCurrency(results!.appreciatedValue)}</span>
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-success/20 bg-success/5">
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground mb-1">Total Return</div>
                    <div className="text-2xl font-bold text-success">
                      {formatCurrency(results!.totalReturn)}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-accent/20 bg-accent/5">
                  <CardContent className="p-4">
                    <div className="text-sm text-muted-foreground mb-1">Net Profit</div>
                    <div className="text-2xl font-bold text-accent">
                      {formatCurrency(results!.netProfit)}
                    </div>
                  </CardContent>
                </Card>

                <Card className="border-primary bg-gradient-to-r from-primary to-primary/80">
                  <CardContent className="p-4">
                    <div className="text-sm text-primary-foreground/80 mb-1">ROI Percentage</div>
                    <div className="text-3xl font-bold text-primary-foreground">
                      {results!.roiPercentage.toFixed(2)}%
                    </div>
                  </CardContent>
                </Card>
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
